import React, { useState, useEffect, useCallback } from "react";

import "./DeleteCartConfirmation.scss";
import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import { getTenantName } from "../../reducers/app";
import {
  getCurrentTrader,
  getCurrentTraderAddresses,
  hasCurrentTrader,
  updateTraderLive,
} from "../../reducers/trader";
import { getCartDefault } from "../../reducers/cartDefault";

import { getToken } from "../../reducers/login";

import { convertDateToApiFormat } from "../../actions/common";

import { createNewCart } from "../../actions/dispatch/dispatchCart";
import { CART_FROM, COMMON_STATUS } from "../../constants/status";
import { clearOnLogout } from "../../actions/dispatch/dispatchCommon";
import history from "../../helper/history";
import { ICartListInfo, IFutureOrder } from "../../interface/ICart";
import { IAddressInfo } from "../../interface/ITrader";
import { updateCartFromLocation } from "../../reducers/cart";
import { ContentLoader } from "../../components/ContentLoader/ContentLoader";

import { getBusinessInfo } from "../../reducers/business";
import { useTranslation } from "react-i18next";

import _ from "underscore";

import OrderList from "../../components/OrderList/OrderList";
import {
  getFutureOrderAsync,
  getFutureOrders,
  getFutureOrderStatus,
} from "../../reducers/futureOrder";

import StandingOrderList from "../../components/StandingOrderList/StandingOrderList";
import { getItemStatus } from "../../reducers/item";
import { getTenantInfo } from "../../reducers/tenant";
import { NoAddressMessage, setFormatMessage } from "../../customhooks/common";
import { hasMultipleCustomers } from "../../reducers/traders";

const DeleteCartConfirmation: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const customer_Name = useSelector(getCurrentTrader);

  const future_orders = useSelector(getFutureOrders);
  const future_order_status = useSelector(getFutureOrderStatus);
  const [future_order_list, setFutureOrderList] = useState<
    IFutureOrder | undefined
  >();

  const client_name: string = useSelector(getTenantName) || "";
  const client_info = useSelector(getTenantInfo);
  const business_info = useSelector(getBusinessInfo);
  const current_customer = useSelector(getCurrentTrader);
  const has_current_customer = useSelector(hasCurrentTrader);

  const [current_date, setCurrentDate] = useState<Date | undefined>(new Date());

  const cart_default = useSelector(getCartDefault);
  const token = useSelector(getToken);
  const [cart_list, setCarts] = useState<ICartListInfo[]>([]);
  const addresses = useSelector(getCurrentTraderAddresses);
  const [current_address, setCurrentAddress] = useState<
    IAddressInfo | undefined
  >();

  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [is_loading_cart_list, setIsLoadingCartList] = useState<boolean>(true);

  const [page_status, setPageStatus] = useState<string>("");

  const item_status = useSelector(getItemStatus);
  const [formattedMessage, setFormattedMessage] = useState<string>('');
  const has_MultipleCustomers = useSelector(hasMultipleCustomers)
  
  useEffect(() => {
    const message = t('CustomerNotLiveError');
    setFormattedMessage(message.replace("{0}", client_info.name ?? ''));
  }, []);

  useEffect(() => {
    sessionStorage.setItem('page', 'cart');
  }, []);

  // Check if customer already selected (from list of customer/if more than 1 customer assigned to user) otherwise redirect to list of customer page
  useEffect(() => {
    if (!has_current_customer) {
      history.push(`${process.env.PUBLIC_URL}/${client_name}`);
    } else {
      if (current_customer?.id) {
        setIsLoading(true);
        setPageStatus(COMMON_STATUS.INITIAL);
      }
    }
  }, [client_name, current_customer?.id, has_current_customer]);

  useEffect(() => {
    if (page_status === COMMON_STATUS.INITIAL) {
      if (cart_default.value.firstAvailableDate) {
        setCurrentDate(new Date(cart_default.value.firstAvailableDate));
      }

      if (cart_default.value.defaultAddressId && addresses) {
        const addr = addresses.find(
          (add) => add.id === cart_default.value.defaultAddressId
        );
        if (addr) {
          setCurrentAddress(addr);
        }
      }

      const params: any = {
        getFutureOrderParams: {
          addressId: cart_default.value.defaultAddressId,
          date: cart_default.value.firstAvailableDate,
        },
        headerParams: {
          addressId: cart_default.value.defaultAddressId,
          dt: cart_default.value.firstAvailableDate,
          tenantName: client_name,
          token: token,
          traderId: current_customer?.id,
        },
      };
      dispatch(getFutureOrderAsync(params));
      setPageStatus(COMMON_STATUS.LOADING);
    }
  }, [
    addresses,
    cart_default.value.defaultAddressId,
    cart_default.value.firstAvailableDate,
    client_name,
    current_customer?.id,
    dispatch,
    page_status,
    token,
  ]);

  useEffect(() => {
    if (
      page_status === COMMON_STATUS.LOADING &&
      future_order_status === COMMON_STATUS.LOADED
    ) {
      setFutureOrderList(future_orders);
         dispatch(updateTraderLive(future_orders.traderLive))
      setIsLoadingCartList(false);
      let carts: ICartListInfo[] = [];
      if (future_orders?.adhocOrders) {
        carts = carts.concat(future_orders.adhocOrders);
      }
      if (future_orders?.lateOrders) {
        carts = carts.concat(future_orders.lateOrders);
      }
      setCarts(
        _.sortBy(carts, (cart: ICartListInfo) => {
          return cart.date || "zzzzz";
        })
      );
      setPageStatus(COMMON_STATUS.LOADED);
    }
  }, [future_order_status, future_orders, page_status]);

  const onGoToDefaultPage = () => {
    history.push(`${process.env.PUBLIC_URL}/${client_name}/set`);
  };

  const onLogout = () => {
    clearOnLogout(dispatch);
  };

  const onGoToCart = useCallback(
    (param: ICartListInfo) => {
      if (cart_default.value.defaultAddressId) {
        const dt = convertDateToApiFormat(current_date);
        const cartProps = {
          traderId: current_customer?.id,
          addressId: cart_default.value.defaultAddressId,
          date: dt,
          savedCartId: param.savedCartId,
          savedCartName: param.savedCartName,
        };

        const headerProps = {
          token: token,
          tenantName: client_name,
          traderId: current_customer?.id,
          addressId: cart_default.value.defaultAddressId,
          isSameDayOrder: param.isSameDayOrder,
          dt: dt,
        };

        const getCartProps = {
          addressId: cart_default.value.defaultAddressId,
          date: dt,
          soAmend: param.soAmend,
          isSameDayOrder: param.isSameDayOrder,
          isLate: param.isLateOrder,
          sequence: param.sequence,
          id: param.id ?? null,
        };

        const getDeliveryRulesProps = {
          traderId: current_customer?.id,
          tenantName: client_name || "",
          token: token,
          addressId: cart_default.value.defaultAddressId,
        };
        createNewCart(
          dispatch,
          cartProps,
          headerProps,
          getCartProps,
          getDeliveryRulesProps
        );
        dispatch(updateCartFromLocation(CART_FROM.CART_DEFAULT));
      }
    },
    [
      cart_default.value.defaultAddressId,
      client_name,
      current_customer?.id,
      current_date,
      dispatch,
      token,
    ]
  );

  useEffect(() => {
    if (item_status === COMMON_STATUS.LOADED) {
      history.push({
        pathname: `${process.env.PUBLIC_URL}/${client_name}/cart`,
      });
    }
  }, [item_status, client_name]);

  
    const onGoToListOfCustomers = () => {
      history.push(`${process.env.PUBLIC_URL}/${client_name}`);
    };
  
    const displayData = () => {
      if (future_order_list?.traderLive == 2 || future_order_list?.traderLive == 0) {
        return <>
          {setFormatMessage(formattedMessage,has_MultipleCustomers, t, onGoToListOfCustomers,onLogout)}
        </>
      }
      else if (future_order_list?.liveAddresses?.length == 0) {
        return <>
          {NoAddressMessage(has_MultipleCustomers, t, onGoToListOfCustomers,onLogout)}
        </>
      }
      else {
        return <>


        <div className="bg-green-400 text-0 mt-4  mb-4 p-3 border-round text-center  ">
          {t("NewCart_PlaceOrder_Validation_OrderDeleted")}
        </div>

        {(future_order_list?.standingOrders &&
          future_order_list?.standingOrders.length > 0) ||
          (cart_list && cart_list.length > 0) ? (
          <>
            <div className="grid">
              <div className="col-12 md-12 xl-8">
                <div className="grid">
                  <div className="col-12 md-7">
                    <div className=" schedule trader-list">
                      <h6 className="mt-4 mb-2">
                        {t("NewCart_PlaceOrder_ListOfFutureOrders")}
                      </h6>
                      {is_loading_cart_list ? (
                        <ContentLoader />
                      ) : (
                        <>
                          {future_order_list?.standingOrders &&
                            future_order_list?.standingOrders.length > 0 && (
                              <StandingOrderList
                                cartDateSelector={current_date || new Date()}
                                carts={future_order_list.standingOrders}
                                business={business_info}
                                onGoToCart={onGoToCart}
                              ></StandingOrderList>
                            )}
                        </>
                      )}
                      {is_loading_cart_list ? (
                        <ContentLoader />
                      ) : (
                        <>
                          {cart_list && cart_list.length > 0 && (
                            <>
                              <div className="list-header mt-5">
                                {t("Cart_Orders")}
                              </div>

                              <OrderList
                                cartDateSelector={current_date || new Date()}
                                carts={cart_list}
                                business={business_info}
                                onGoToCart={onGoToCart}
                              ></OrderList>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <></>
        )}

        <div className="flex justify-content-center flex-wrap card-container yellow-container mb-4 mt-4">
          <Button
            onClick={onGoToDefaultPage}
            className="flex align-items-center justify-content-center p-button p-component p-button-success mr-2 mb-2"
          >
            {t("NewCart_PlaceNewOrder")}
          </Button>
          <Button
            onClick={onLogout}
            className="flex align-items-center justify-content-center p-button p-component p-button-warning mr-2 mb-2"
          >
            {t("NewCart_Logout")}
          </Button>
        </div>

      </>
  
      }
    }
  

  return (
    <div className="p-2">
      <div className="mt-4">
        <h6>
          {customer_Name?.name && <>{customer_Name.name}</>}
          <span className="font-normal">
            {current_address?.address1 && (
              <>
                <br />
                {current_address?.address1}
              </>
            )}
            {current_address?.address2 && (
              <>
                <br />
                {current_address?.address2}
              </>
            )}
            {(current_address?.city || current_address?.post) && (
              <>
                <br />
                {current_address?.city}{" "}
                {current_address?.post && <> {current_address?.post}</>}
              </>
            )}
          </span>
        </h6>

      {displayData()}
        

      </div>
    </div>

  );
};

export default DeleteCartConfirmation;
